import CustomButton from "@/components/CustomButton";
import { useUpdateNpvLockedValue } from "@/hooks/useUpdateNpvLockedValue";
import { HouseholdDataType } from "@/types/enums";
import { useQueryClient } from "@tanstack/react-query";
import { Tooltip } from "devextreme-react";
import { IPositionProps, Position } from "devextreme-react/tooltip";
import { formatNumber } from "devextreme/localization";
import { useState } from "react";
import { useParams } from "react-router";

type Props = {
  dataType: HouseholdDataType;
  initialValue: number;
  lockText: string;
  lockTooltipPosition: IPositionProps;
  isFetching?: boolean;
  externalCode: string;
  lockedInValue?: number | null;
};

const LockComponent: React.FC<Props> = ({
  dataType,
  initialValue,
  lockText,
  lockTooltipPosition,
  isFetching,
  externalCode,
  lockedInValue,
}) => {
  const { id } = useParams();
  const qc = useQueryClient();

  const [isLocking, setIsLocking] = useState(false);
  const {
    mutateAsync: mutateAsyncNpv,
    isPending: isNpvValueMutating,
    isError,
  } = useUpdateNpvLockedValue(isLocking);
  const roundedValue = initialValue?.toFixed(2);

  const lockValue = async () => {
    setIsLocking(true);
    await mutateAsyncNpv({
      externalCode,
      lockedNPV: Number(roundedValue),
    });
    await qc.refetchQueries({ queryKey: ["unique-possibilities", id] });
  };

  const unlockValue = async () => {
    setIsLocking(false);
    await mutateAsyncNpv({
      externalCode,
      lockedNPV: null,
    });
    await qc.refetchQueries({ queryKey: ["unique-possibilities", id] });
  };

  return (
    <>
      <CustomButton
        disabled={isFetching || isNpvValueMutating}
        variant="primary"
        text={
          isFetching || isNpvValueMutating
            ? "Loading..."
            : lockedInValue || lockedInValue === 0
              ? `Unlock ${lockText}`
              : `Lock ${lockText}`
        }
        onClickHandler={lockedInValue || lockedInValue == 0 ? unlockValue : lockValue}
      />
      {lockedInValue || lockedInValue === 0 ? (
        <div className="flex items-center">
          <i id={`lock-${dataType}`} className={`dx-icon-info text-2xl !text-primary`} />
          <Tooltip
            showEvent="mouseenter"
            hideEvent="mouseleave"
            hideOnOutsideClick={false}
            target={`#lock-${dataType}`}
          >
            <Position {...lockTooltipPosition} />
            {isError ? `Error fetching ${lockText}` : null}
            {lockedInValue || lockedInValue === 0
              ? `Already locked in ${lockText}: ${formatNumber(lockedInValue, "$ #,##0")}`
              : null}
          </Tooltip>
        </div>
      ) : null}
    </>
  );
};

export default LockComponent;
